// frontend/src/pages/manufacturing/v2/KPIDashboardV2.jsx
//
// Trailing-window KPI dashboard for the V2 manufacturing module. Reads
// GET /api/v2/dashboard/kpis/?days=7|30|90 and renders:
//   - a 7/30/90-day period toggle (default 30) that refetches on change;
//   - KPI cards (throughput units, on-time %, scrap %, avg cycle time, WIP,
//     orders shipped + on-time %); nullable KPIs render an em dash;
//   - a production-per-day bar chart (units, with a thinner scrap bar) built
//     with recharts (already a dependency — no new packages);
//   - two tables: top items produced and busiest operations.
// Matches v2 page styling (GenerationModeBadge header) with loading + error.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Button, Alert, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from 'recharts';
import { v2 } from '../../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';

const DAY_OPTIONS = [7, 30, 90];

const num = (v) => Number(v || 0).toLocaleString();
// Render nullable percentage metrics as an em dash. Zero is a real value, so
// only null / undefined collapse to "—".
const pctOrDash = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

/** A single KPI card. */
function KpiCard({ label, value, sub, testId }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}
      data-testid={testId}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      {sub != null && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

/** Short "MM/DD" label for the x-axis so dense windows stay readable. */
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const KPIDashboardV2 = () => {
  usePageTitle('KPI Dashboard V2');

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (windowDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.dashboard.kpis({ days: windowDays });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load KPI dashboard:', err);
      setError('Failed to load KPI data');
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(days); }, [fetchData, days]);

  const handleDaysChange = (_e, next) => {
    // ToggleButtonGroup fires null when the active button is re-clicked; ignore.
    if (next == null) return;
    setDays(next);
  };

  const throughput = data?.throughput || {};
  const perDay = throughput.per_day || [];
  const wip = data?.wip || {};
  const orders = data?.orders || {};
  const topItems = data?.top_items || [];
  const busiest = data?.busiest_operations || [];

  const chartData = perDay.map((d) => ({
    label: shortDate(d.date),
    units: Number(d.units || 0),
    scrap: Number(d.scrap || 0),
  }));

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          KPIs
          <GenerationModeBadge />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={days}
            exclusive
            size="small"
            onChange={handleDaysChange}
            data-testid="period-toggle"
          >
            {DAY_OPTIONS.map((d) => (
              <ToggleButton key={d} value={d} data-testid={`period-${d}`}>
                {d} days
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchData(days)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} data-testid="loading-state" />}
      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="error-state">{error}</Alert>}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }} data-testid="kpi-cards">
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Throughput"
                value={num(throughput.total_units)}
                sub="units produced"
                testId="kpi-throughput"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="On-time completion"
                value={pctOrDash(data.on_time_rate_pct)}
                sub={`${num(data.completed_wos)} WOs completed`}
                testId="kpi-ontime"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Scrap rate"
                value={pctOrDash(data.scrap_rate_pct)}
                testId="kpi-scrap"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Avg cycle time"
                value={data.avg_cycle_time_days == null ? '—' : `${Number(data.avg_cycle_time_days).toFixed(1)}d`}
                sub="days per WO"
                testId="kpi-cycle"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="WIP"
                value={num(wip.work_orders)}
                sub={`${num(wip.units)} units in progress`}
                testId="kpi-wip"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                label="Orders shipped"
                value={num(orders.shipped)}
                sub={orders.shipped_on_time_pct == null
                  ? 'on-time —'
                  : `${Number(orders.shipped_on_time_pct).toFixed(1)}% on-time`}
                testId="kpi-orders"
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Production per day</Typography>
            {chartData.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }} data-testid="chart-empty">
                No production in this window.
              </Typography>
            ) : (
              <Box sx={{ width: '100%', height: 320 }} data-testid="production-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      interval="preserveStartEnd"
                      minTickGap={24}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="units" name="Units" fill="#1976d2" barSize={14} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="scrap" name="Scrap" fill="#ef5350" barSize={7} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Top items produced</Typography>
                {topItems.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }} data-testid="top-items-empty">
                    No items produced in this window.
                  </Typography>
                ) : (
                  <Table size="small" data-testid="top-items-table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Units</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topItems.map((it, idx) => (
                        <TableRow key={`${it.item_code}-${idx}`} data-testid={`top-item-${idx}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {it.item_name || it.item_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {it.item_code}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{num(it.units)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Busiest operations</Typography>
                {busiest.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }} data-testid="busiest-empty">
                    No completed steps in this window.
                  </Typography>
                ) : (
                  <Table size="small" data-testid="busiest-table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Operation</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Steps</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Units</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {busiest.map((op, idx) => (
                        <TableRow key={`${op.operation_code}-${idx}`} data-testid={`busiest-${idx}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {op.operation_name || op.operation_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {op.operation_code}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{num(op.completed_steps)}</TableCell>
                          <TableCell align="right">{num(op.units)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default KPIDashboardV2;
